import { Link } from 'react-router-dom'
import { Trophy } from 'lucide-react'
import {
  formatGoalPercent,
  sortTeamByRevenue,
  type TimeTabView,
} from '@/components/Dashboard/timeTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'
import { cn } from '@/utils/cn'

type Props = {
  view: TimeTabView
  valuesVisible?: boolean
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

const MEDAL_CLASSES = [
  'text-amber-500',
  'text-slate-400',
  'text-orange-500',
]

export function TimeCloserRankingTable({ view, valuesVisible = false }: Props) {
  const ranking = sortTeamByRevenue(view.teamMembers)

  return (
    <div className='rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] shadow-sm'>
      <div className='flex flex-wrap items-end justify-between gap-3 border-b border-[var(--border)] px-5 py-4'>
        <div>
          <div className='flex items-center gap-2'>
            <Trophy size={16} className='text-amber-500' aria-hidden />
            <h3 className='text-base font-semibold text-text-primary'>Ranking comercial</h3>
          </div>
          <p className='mt-1 text-sm text-text-muted'>
            Receita fechada, meta individual e pipeline ativo no período (closers e admins)
          </p>
        </div>
        <Link
          to='/closer-leads'
          className='rounded-full border border-[var(--border)] px-3 py-1 text-xs font-semibold text-text-secondary hover:bg-[var(--bg-dark)]/20'
        >
          Abrir carteira closer
        </Link>
      </div>

      {!ranking.length ? (
        <p className='px-5 py-10 text-center text-sm text-text-muted'>
          Ninguém no time comercial com movimento no período selecionado.
        </p>
      ) : (
        <div className='overflow-x-auto'>
          <table className='min-w-full text-left text-sm'>
            <thead>
              <tr className='border-b border-[var(--border)] text-xs uppercase tracking-wide text-text-muted'>
                <th className='px-5 py-3 font-semibold'>#</th>
                <th className='px-3 py-3 font-semibold'>Closer</th>
                <th className='px-3 py-3 font-semibold'>Deals</th>
                <th className='px-3 py-3 font-semibold'>Receita</th>
                <th className='px-3 py-3 font-semibold'>Meta</th>
                <th className='px-3 py-3 font-semibold'>Pipeline</th>
                <th className='px-5 py-3 font-semibold'>Atingimento</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((member, index) => {
                const goalPct = member.goal > 0 ? (member.achieved / member.goal) * 100 : 0
                return (
                  <tr
                    key={member.id}
                    className='border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-dark)]/10'
                  >
                    <td className='px-5 py-3'>
                      <span
                        className={cn(
                          'font-bold tabular-nums',
                          MEDAL_CLASSES[index] ?? 'text-text-muted',
                        )}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className='px-3 py-3'>
                      <div className='flex items-center gap-2.5'>
                        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[var(--border)] bg-[var(--bg-dark)]/10 text-xs font-bold text-text-primary'>
                          {initials(member.name)}
                        </span>
                        <span className='font-medium text-text-primary'>{member.name}</span>
                      </div>
                    </td>
                    <td className='px-3 py-3 tabular-nums text-text-secondary'>
                      {formatDashboardNumber(member.deals, valuesVisible)}
                    </td>
                    <td className='px-3 py-3 font-semibold tabular-nums text-text-primary'>
                      {formatDashboardCurrency(member.revenue, valuesVisible)}
                    </td>
                    <td className='px-3 py-3 tabular-nums text-text-secondary'>
                      {formatDashboardCurrency(member.goal, valuesVisible)}
                    </td>
                    <td className='px-3 py-3 tabular-nums text-text-secondary'>
                      {formatDashboardCurrency(member.pipelineValue, valuesVisible)}
                      <span className='ml-1 text-xs text-text-muted'>
                        ({formatDashboardNumber(member.pipelineCount, valuesVisible)})
                      </span>
                    </td>
                    <td className='px-5 py-3'>
                      <div className='min-w-[120px]'>
                        <div className='flex items-center justify-between gap-2 text-xs'>
                          <span className='font-semibold tabular-nums text-text-primary'>
                            {formatGoalPercent(goalPct, valuesVisible)}
                          </span>
                        </div>
                        <div className='mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--bg-dark)]/15'>
                          <div
                            className={cn(
                              'h-full rounded-full transition-all',
                              goalPct >= 100 ? 'bg-emerald-500' : 'bg-[var(--brand-primary)]',
                            )}
                            style={{ width: `${Math.min(goalPct, 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
